import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { BookOpen, Sparkles, Users, Crown, Heart, Moon, Sun, Handshake, Sprout, Send } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, nightMode, toggleNightMode, user } = useContext(AuthContext);
  const [showAuth, setShowAuth] = useState(false);
//  const [showLegacyLogin, setShowLegacyLogin] = useState(false);
//  const [formData, setFormData] = useState({ email: '', password: '' });
//  const [loading, setLoading] = useState(false);

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (user && user.id !== 'guest-user') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Canonicalize to non-www to ensure consistent auth behavior and prevent cookie mismatches
  useEffect(() => {
    if (window.location.hostname === 'www.dreamcanvasmaker.com') {
      window.location.replace(window.location.href.replace('www.dreamcanvasmaker.com', 'dreamcanvasmaker.com'));
    }
  }, []);

  useEffect(() => {
    if (searchParams.get('login') === 'true') {
      setShowAuth(true);
    }
  }, [searchParams]);

  const handleGoogleSignIn = () => {
    // Clear any existing auth state/cookies that might cause conflicts
    // This helps prevent "invalid_state_parameter" errors from stale sessions
    let cookieDomain = window.location.hostname;
    if (cookieDomain.endsWith('dreamcanvasmaker.com')) {
      cookieDomain = '.dreamcanvasmaker.com';
    }
    document.cookie = `session_token=; domain=${cookieDomain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    // Also clear without domain to catch host-only cookies
    document.cookie = `session_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    localStorage.removeItem('token');
    
    // Redirect to Emergent Auth with dashboard as redirect URL
    // Adding timestamp to help prevent state caching issues
    const origin = window.location.origin;
    const redirectUrl = `${origin}/dashboard`;
    const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
    const nonce = Date.now(); // Prevent browser caching of auth redirect
    window.location.href = `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}&_t=${nonce}`;
  };

  // const handleLegacyLogin = async (e) => {
  //   e.preventDefault();
  //   setLoading(true);
    
  //   try {
  //     const response = await axios.post(`${API}/auth/login`, formData);
  //     login(response.data.token, response.data.user);
  //     toast.success('Welcome back!');
  //     navigate('/dashboard');
  //   } catch (error) {
  //     toast.error(error.response?.data?.detail || 'Authentication failed');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleGuestMode = async () => {
    try {
      const response = await axios.get(`${API}/auth/guest`);
      login(response.data.token, response.data.user);
      toast.info('Exploring in Guest Mode');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to enter guest mode');
    }
  };

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
      {/* Header */}
      <header className="p-2 sm:p-4 flex justify-between items-center">
        <div className="flex items-center gap-2 sm:gap-3">
          <img
            src="/icons/DCM-logo.png"
            alt="by Dream Canvas Maker"
            className={`w-8 h-8 sm:w-10 sm:h-10 object-contain rounded transition-all duration-200 ${
              nightMode ? 'invert brightness-0' : ''
            }`}
          />
          <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            StoryTime Studio
          </h1>
        </div>
        <Button
          data-testid="night-mode-toggle"
          onClick={toggleNightMode}
          variant="outline"
          size="icon"
          className="rounded-full w-10 h-10 sm:w-12 sm:h-12"
        >
          {nightMode ? <Sun className="w-4 h-4 sm:w-5 sm:h-5" /> : <Moon className="w-4 h-4 sm:w-5 sm:h-5" />}
        </Button>
      </header>

      {/* Hero Section */}
      <main className="container mx-auto px-6 py-8 sm:py-12">
        <div className="text-center max-w-4xl mx-auto">
          <h2 
            className="text-4xl md:text-4xl lg:text-4xl font-extrabold mb-1"
            style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)', lineHeight: '1.2' }}
          >
            Create Personalized Stories in Seconds
          </h2>
          <h2 
            className="text-2xl md:text-2xl lg:text-2xl font-extrabold mb-5"
            style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)', lineHeight: '1.2' }}
          >
            complete with illustrations and life lessons
          </h2>
          <p className="text-2xl md:text-2xl mb-8 text-[var(--text-secondary)]">
            Turn your child into the hero of their own tale! Generate unique, personalized stories with beautiful illustrations, designed to teach life lessons and spark imagination.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 justify-center mb-10">
            <Button
              data-testid="get-started-btn"
              onClick={() => setShowAuth(true)}
              className="pill-button text-lg text-white"
              style={{ background: 'var(--accent-2)' }}
              size="lg"
            >
              <Sparkles className="mr-2 w-5 h-5" />
              Sign Up / Sign In
            </Button>
            <Button
              data-testid="try-guest-mode-btn"
              onClick={handleGuestMode}
              variant="outline"
              className="pill-button text-lg"
              style={{ 
                color: nightMode ? '#1a202c' : 'var(--text-primary)',
                background: nightMode ? '#f0e8f5' : 'white',
                borderColor: nightMode ? '#c4c4c4' : 'var(--sketch-border)'
              }}
              size="lg"
            >
              View app as Guest
            </Button>
            <Button
              onClick={() => navigate('/community')}
              variant="outline"
              size="lg"
              className="pill-button text-lg"
              style={{ 
                color: nightMode ? '#1a202c' : 'var(--text-primary)',
                background: nightMode ? '#f0e8f5' : 'white',
                borderColor: nightMode ? '#c4c4c4' : 'var(--sketch-border)'
              }}
            >
              <Users className="mr-2 w-5 h-5" />
              Community Stories
            </Button>
            <Button
              onClick={() => navigate('/subscription')}
              variant="outline"
              size="lg"
              className="pill-button text-lg"
              style={{ 
                color: nightMode ? '#1a202c' : 'var(--text-primary)',
                background: nightMode ? '#f0e8f5' : 'white',
                borderColor: nightMode ? '#c4c4c4' : 'var(--sketch-border)'
              }}
            >
              <Crown className="mr-2 w-5 h-5" />
              Pricing
            </Button>
          </div>

          {/* How It Works Section */}
          <div className="mt-12">
            <h3 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>How It Works</h3>
            <div className="grid md:grid-cols-3 gap-2">
              <div className="story-card p-4">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center text-2xl font-bold text-white" style={{ background: 'var(--accent-1)' }}>1</div>
                <h4 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Create Characters</h4>
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Build stylised profiles for your child and other characters, making them the star of stories.</p>
              </div>
              <div className="story-card p-4">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center text-2xl font-bold text-white" style={{ background: 'var(--accent-2)' }}>2</div>
                <h4 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Choose a Topic</h4>
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Select a life lesson or topic, such as 'courage', or select from our curated folio stories.</p>
              </div>
              <div className="story-card p-4">
                <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center text-2xl font-bold text-white" style={{ background: 'var(--accent-3)' }}>3</div>
                <h4 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Generate, Read & Discuss</h4>
                <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Our app crafts a kids-safe, unique story and illustration in moments, with discussion prompts.</p>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="mt-12">
            <h3 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>A Universe of Features</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <div className="story-card p-4">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-1)' }}>
                <Users className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Create Your Family</h4>
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Build digital avatars of your child, family, and pets to star in every story.</p>
            </div>

            <div className="story-card p-4">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-4)' }}>
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Endless Adventures</h4>
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Generate unique stories from click, tailored to your child's reading level and interests.</p>
            </div>

            <div className="story-card p-4">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-5)' }}>
                <Sprout className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Learn and Grow</h4>
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Each story is crafted to teach valuable life lessons, from sharing to problem-solving.</p>
            </div>

            <div className="story-card p-4">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-2)' }}>
                <Handshake className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Parent Helpers</h4>
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Get discussion prompts to reinforce each topic and use learning profile for a summary of topics & vocabulary.</p>
            </div>

            <div className="story-card p-4">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-3)' }}>
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Folio Stories</h4>
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Select from and personalize a curated list of story series aimed at teaching key topics, such as digital wellbeing.</p>
            </div>

            <div className="story-card p-4">
              <div className="w-16 h-16 mx-auto mb-2 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-1)' }}>
                <Send className="w-8 h-8 text-white" />
              </div>
              <h4 className="text-lg font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Share with Loved Ones</h4>
              <p className="text-lg" style={{ color: 'var(--text-secondary)' }}>Easily share your favorite stories and illustrations with family and friends, no matter where they are.</p>
            </div>
          </div>
        </div>
        </div>
      </main>

      {/* Auth Dialog - Google OAuth */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className={`card-container ${nightMode ? 'night-card' : ''}`} style={{ maxWidth: '450px' }}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center mb-1">
              Welcome to StoryTime Studio
            </DialogTitle>
            <DialogDescription className="text-sm text-center text-[var(--text-secondary)] mb-4">
              by Dream Canvas Maker
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Google Sign In Button */}
            <Button
              data-testid="google-signin-btn"
              onClick={handleGoogleSignIn}
              className="w-full pill-button text-white flex items-center justify-center gap-3 py-6"
              style={{ background: 'var(--accent-2)' }}
            >
              {/* <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg> */}
              Sign Up or Sign in with Google
            </Button>

            {/* <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or</span>
              </div>
            </div> */}

            {/* Legacy login for existing users */}
            {/* <Button
              data-testid="legacy-login-toggle-btn"
              onClick={() => setShowLegacyLogin(!showLegacyLogin)}
              variant="outline"
              className="w-full pill-button"
            >
              {showLegacyLogin ? 'Hide' : 'Existing users: Sign in with email'}
            </Button>

            {showLegacyLogin && (
              <form onSubmit={handleLegacyLogin} className="space-y-3 mt-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    data-testid="legacy-email-input"
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    data-testid="legacy-password-input"
                    id="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <Button
                  data-testid="legacy-login-submit-btn"
                  type="submit"
                  className="w-full pill-button text-white"
                  disabled={loading}
                  style={{ background: 'var(--accent-2)' }}
                >
                  {loading ? 'Please wait...' : 'Sign In'}
                </Button>
              </form>
            )} */}
          </div>
          
          <p className="text-center text-xs mt-4 text-gray-500">
            Sign in with Google to create an account
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;